import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ArrowUpDown, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Product {
  id: string;
  agent_id: string;
  name: string;
  model: string | null;
  description: string | null;
  unit_price: number;
  quantity: number;
  total_price: number | null;
  status: string;
  rejection_reason: string | null;
  seo_score: number | null;
  seo_keywords: string[] | null;
  created_at: string;
}

interface ProductsTableProps {
  products: Product[];
  onApprove: (productId: string) => Promise<void>;
  onReject: (productId: string) => Promise<void>;
  onRefresh: () => void;
}

type SortField = 'name' | 'seo_score' | 'total_price' | 'created_at';
type SortOrder = 'asc' | 'desc';

export const ProductsTable = ({ products, onApprove, onReject, onRefresh }: ProductsTableProps) => {
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.model?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    
    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortField === 'created_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === sortedProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(sortedProducts.map(p => p.id)));
    }
  };

  const toggleSelect = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleBulkApprove = async () => {
    const promises = Array.from(selectedProducts).map(id => onApprove(id));
    await Promise.all(promises);
    setSelectedProducts(new Set());
    onRefresh();
    toast.success(`Approved ${selectedProducts.size} products`);
  };

  const handleBulkReject = async () => {
    const reason = prompt('Enter rejection reason for all selected products:');
    if (!reason) return;

    const promises = Array.from(selectedProducts).map(id => onReject(id));
    await Promise.all(promises);
    setSelectedProducts(new Set());
    onRefresh();
    toast.success(`Rejected ${selectedProducts.size} products`);
  };

  const getScoreBadgeVariant = (score: number | null) => {
    if (!score) return 'secondary';
    if (score >= 80) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Submissions</CardTitle>
        
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selectedProducts.size > 0 && (
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              onClick={handleBulkApprove}
              className="gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Approve Selected ({selectedProducts.size})
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkReject}
              className="gap-2"
            >
              <XCircle className="h-4 w-4" />
              Reject Selected ({selectedProducts.size})
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {sortedProducts.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No products found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedProducts.size === sortedProducts.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Date
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('seo_score')}
                  >
                    <div className="flex items-center gap-2">
                      SEO Score
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Price Breakdown</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProducts.map((product) => {
                  const isSelected = selectedProducts.has(product.id);
                  
                  return (
                    <TableRow 
                      key={product.id}
                      className={isSelected ? 'bg-muted/50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(product.id)}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(product.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell>{product.model || '-'}</TableCell>
                      <TableCell>
                        {product.seo_score ? (
                          <div className="space-y-1">
                            <Badge variant={getScoreBadgeVariant(product.seo_score)}>
                              {product.seo_score}/100
                            </Badge>
                            {product.seo_keywords && product.seo_keywords.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {product.seo_keywords.slice(0, 2).join(', ')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          <div>Unit: €{product.unit_price.toFixed(2)}</div>
                          <div className="text-muted-foreground">Qty: {product.quantity}</div>
                          {product.total_price && (
                            <div className="font-semibold">Total: €{product.total_price.toFixed(2)}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          product.status === 'approved' ? 'default' :
                          product.status === 'rejected' ? 'destructive' : 'secondary'
                        }>
                          {product.status}
                        </Badge>
                        {product.rejection_reason && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {product.rejection_reason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {product.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => onApprove(product.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => onReject(product.id)}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
